import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { http } from "../lib/api";
import { MaskLines } from "../components/Motion";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "checking" : "error");

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") { setStatus("paid"); return; }
        if (data?.status === "expired" || data?.status === "failed") { setStatus("error"); return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries >= 10) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="min-h-[100svh] bg-[#0B0B0B] text-white flex items-center px-5 sm:px-10 lg:px-16 pt-28 pb-16">
      {status === "checking" && (
        <div className="flex items-center gap-4 font-display uppercase text-3xl" data-testid="payment-checking"><Loader2 className="w-8 h-8 animate-spin text-[#E10600]" /> Confirming your order...</div>
      )}
      {status === "paid" && (
        <div data-testid="payment-paid">
          <CheckCircle2 className="w-14 h-14 text-[#E10600]" />
          <h1 className="font-display uppercase text-6xl sm:text-8xl leading-[0.88] mt-6">
            <MaskLines lines={["You're in", <span key="s" className="text-[#E10600]">the squad.</span>]} />
          </h1>
          <p className="mt-6 max-w-md text-lg font-semibold text-zinc-300">Payment confirmed. A receipt is on its way to your email and your hat ships soon.</p>
          <Link to="/" data-testid="success-home-link" className="mt-10 inline-block bg-[#E10600] px-8 py-4 font-display uppercase text-xl transition-colors duration-200 hover:bg-white hover:text-[#0B0B0B]">Back to the party</Link>
        </div>
      )}
      {(status === "error" || status === "timeout") && (
        <div data-testid="payment-error">
          <XCircle className="w-14 h-14 text-[#E10600]" />
          <h1 className="font-display uppercase text-5xl sm:text-7xl mt-6">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="mt-4 text-zinc-300 font-semibold">{status === "timeout" ? "Check your email for a receipt in a few minutes." : "No charge was made. You can try again anytime."}</p>
          <Link to="/shop" className="mt-8 inline-block bg-white text-[#0B0B0B] px-8 py-4 font-display uppercase text-xl" data-testid="error-shop-link">Back to shop</Link>
        </div>
      )}
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main data-testid="payment-cancel-page" className="min-h-[100svh] bg-[#F6F3EF] flex items-center px-5 sm:px-10 lg:px-16 pt-28 pb-16">
      <div>
        <h1 className="font-display uppercase text-6xl sm:text-8xl leading-[0.88]">Checkout <span className="text-[#E10600]">canceled.</span></h1>
        <p className="mt-6 text-lg font-semibold">No worries — your hat's still waiting.</p>
        <Link to="/shop" data-testid="cancel-shop-link" className="mt-8 inline-block bg-[#E10600] text-white px-8 py-4 font-display uppercase text-xl transition-colors duration-200 hover:bg-[#0B0B0B]">Back to shop</Link>
      </div>
    </main>
  );
}
